import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, FileText, X, Truck } from 'lucide-react';

const gold = '#DBAE6E';

function Footer() {
  const [activeTab, setActiveTab] = useState(null);

  const openTab = (tab) => setActiveTab(tab);
  const closeTab = () => setActiveTab(null);

  // Animation variants for tabs
  const tabVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
  };

  // Animation variants for modal
  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  const contentVariants = {
    hidden: { scale: 0.95, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } },
    exit: { scale: 0.95, opacity: 0, transition: { duration: 0.2 } },
  };

  return (
    <footer className="bg-primary dark:bg-primary-dark flex text-black dark:text-white px-1 sm:px-1">
      <div className="max-w-7xl mx-auto">
        {/* Centered Tabs */}
        <div className="flex flex-row sm:flex-row justify-center gap-4 sm:gap-8 mb-6">
          <motion.div
            variants={tabVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-row items-center cursor-pointer group py-2"
            onClick={() => openTab('terms')}
            role="button"
            tabIndex={0}
            aria-label="View Terms and Conditions"
            onKeyDown={(e) => e.key === 'Enter' && openTab('terms')}
          >
            <FileText
              size={16}
              className="text-gold mr-2 group-hover:scale-110 transition-transform duration-300"
              style={{ color: gold }}
            />
            <span
              className="text-xs sm:text-sm font-medium border-b-2 group-hover:text-gold transition-colors duration-300"
              style={{ borderColor: gold }}
            >
              Terms & Conditions
            </span>
          </motion.div>

          <motion.div
            variants={tabVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-row items-center cursor-pointer group py-2"
            onClick={() => openTab('privacy')}
            role="button"
            tabIndex={0}
            aria-label="View Privacy Policy"
            onKeyDown={(e) => e.key === 'Enter' && openTab('privacy')}
          >
            <Eye
              size={16}
              className="text-gold mr-2 group-hover:scale-110 transition-transform duration-300"
              style={{ color: gold }}
            />
            <span
              className="text-xs sm:text-sm font-medium border-b-2 group-hover:text-gold transition-colors duration-300"
              style={{ borderColor: gold }}
            >
              Privacy Policy
            </span>
          </motion.div>

          <motion.div
            variants={tabVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-row items-center cursor-pointer group py-2"
            onClick={() => openTab('exchange')}
            role="button"
            tabIndex={0}
            aria-label="View Exchange Policy"
            onKeyDown={(e) => e.key === 'Enter' && openTab('exchange')}
          >
            <Truck
              size={16}
              className="text-gold mr-2 group-hover:scale-110 transition-transform duration-300"
              style={{ color: gold }}
            />
            <span
              className="text-xs sm:text-sm font-medium border-b-2 group-hover:text-gold transition-colors duration-300"
              style={{ borderColor: gold }}
            >
              Exchange Policy
            </span>
          </motion.div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {activeTab && (
          <motion.div
            className="fixed inset-0 bg-primary dark:bg-primary-dark/95 z-50 flex items-center justify-center p-4 sm:p-6"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="dialog"
            aria-modal="true"
            aria-label={
              activeTab === 'terms'
                ? 'Terms and Conditions'
                : activeTab === 'privacy'
                ? 'Privacy Policy'
                : 'Exchange Policy'
            }
          >
            <motion.div
              className="relative w-full max-w-2xl bg-primary dark:bg-primary-dark/90 border border-gold/30 rounded-xl shadow-2xl flex flex-col max-h-[85vh]"
              variants={contentVariants}
            >
              {/* Fixed Header */}
              <div className="flex-shrink-0 p-5 sm:p-6 border-b border-gold/20">
                <h2
                  className="text-xl sm:text-2xl font-bold text-center"
                  style={{ color: gold }}
                >
                  {activeTab === 'terms'
                    ? 'Terms and Conditions'
                    : activeTab === 'privacy'
                    ? 'Privacy Policy'
                    : 'Exchange Policy'}
                </h2>
                <motion.button
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white text-black flex items-center justify-center shadow-md hover:bg-gold/80 transition-colors"
                  onClick={closeTab}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Close modal"
                >
                  <X size={16} />
                </motion.button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-5 sm:p-6">
                <div className="text-black dark:text-white/90 text-xs sm:text-sm leading-relaxed space-y-3">
                  {activeTab === 'terms' ? (
                    <>
                      <p>
                        Welcome to Layne Red. These Terms and Conditions govern your use of
                        our website and the purchase of products from us. By accessing or placing an
                        order, you agree to be bound by these terms.
                      </p>

                      <h3 className="text-base font-semibold text-gold">1. Order Cancellation</h3>
                      <p>
                        Orders cannot be canceled on the day following the purchase. Once an order is
                        confirmed, it is processed immediately, and no cancellations are permitted after
                        the initial day of placing the order.
                      </p>

                      <h3 className="text-base font-semibold text-gold">2. Exchanges</h3>
                      <p>
                        For details on exchanges, see our{' '}
                        <span
                          className="text-gold hover:underline cursor-pointer"
                          onClick={() => openTab('exchange')}
                        >
                          Exchange Policy
                        </span>
                        .
                      </p>

                      <h3 className="text-base font-semibold text-gold">3. Returns</h3>
                      <p>
                        Returns are not accepted unless the product is damaged or defective upon
                        receipt. If you receive a damaged or defective item, please contact us within
                        three (3) days of delivery to initiate a return or replacement process.
                      </p>

                      <h3 className="text-base font-semibold text-gold">4. Pricing and Payment</h3>
                      <p>
                        All prices are listed in INR and are subject to change without notice. We
                        accept various payment methods, and payment must be completed at the time of
                        order. We are not responsible for any additional charges imposed by your
                        payment provider.
                      </p>

                      <h3 className="text-base font-semibold text-gold">5. Shipping</h3>
                      <p>
                        We aim to deliver products within the estimated timeframe provided at checkout.
                        Delays due to unforeseen circumstances (e.g., weather, logistics issues) are
                        not our responsibility. Shipping costs are non-refundable.
                      </p>

                      <h3 className="text-base font-semibold text-gold">6. Intellectual Property</h3>
                      <p>
                        All content on our website, including images, logos, and descriptions, is the
                        property of Layne Red and may not be used without permission.
                      </p>

                      <h3 className="text-base font-semibold text-gold">7. Limitation of Liability</h3>
                      <p>
                        Layne Red is not liable for any indirect, incidental, or consequential
                        damages arising from the use of our products or website. Our liability is
                        limited to the amount paid for the product.
                      </p>
{/* 
                      <h3 className="text-base font-semibold text-gold">8. Governing Law</h3>
                      <p>
                        These terms are governed by the laws of India. Any disputes will be resolved
                        in the courts of [Your City], India.
                      </p> */}

                      <p className="mt-5">
                        For questions, contact us at{' '}
                        <a
                          href="mailto:official.laynered@gmail.com"
                          className="text-gold hover:underline"
                          style={{ color: gold }}
                        >
                          official.laynered@gmail.com
                        </a>
                        .
                      </p>
                    </>
                  ) : activeTab === 'privacy' ? (
                    <>
                      <p>
                        At Layne Red, we value your privacy and are committed to protecting
                        your personal information. This Privacy Policy explains how we collect, use,
                        and safeguard your data.
                      </p>

                      <h3 className="text-base font-semibold text-gold">1. Information We Collect</h3>
                      <p>
                        We collect the following information when you interact with our website or
                        services:
                      </p>
                      <ul className="list-disc pl-5">
                        <li>
                          Personal details (e.g., name, email, phone number, shipping address) when
                          you place an order or create an account.
                        </li>
                        <li>
                          Payment information (processed securely via third-party providers; we do not
                          store card details).
                        </li>
                        <li>
                          Browsing data (e.g., IP address, device type, pages visited) via cookies and
                          analytics tools.
                        </li>
                      </ul>

                      <h3 className="text-base font-semibold text-gold">2. How We Use Your Data</h3>
                      <p>Your information is used to:</p>
                      <ul className="list-disc pl-5">
                        <li>Process and fulfill your orders.</li>
                        <li>Communicate order updates or customer support responses.</li>
                        <li>Improve our website and personalize your experience.</li>
                        <li>
                          Send promotional emails (only with your consent; you can opt out anytime).
                        </li>
                      </ul>

                      <h3 className="text-base font-semibold text-gold">3. Data Sharing</h3>
                      <p>
                        We do not sell your personal information. We may share data with:
                      </p>
                      <ul className="list-disc pl-5">
                        <li>
                          Third-party service providers (e.g., payment processors, shipping partners)
                          to fulfill orders.
                        </li>
                        <li>Legal authorities if required by law.</li>
                      </ul>

                      <h3 className="text-base font-semibold text-gold">4. Data Security</h3>
                      <p>
                        We use industry-standard encryption (e.g., SSL) to protect your data during
                        transmission. However, no system is 100% secure, and we cannot guarantee
                        absolute security.
                      </p>

                      <h3 className="text-base font-semibold text-gold">5. Cookies</h3>
                      <p>
                        Our website uses cookies to enhance functionality and analyze usage. You can
                        manage cookie preferences via your browser settings.
                      </p>

                      <h3 className="text-base font-semibold text-gold">6. Your Rights</h3>
                      <p>
                        You have the right to:
                      </p>
                      <ul className="list-disc pl-5">
                        <li>Access or correct your personal data.</li>
                        <li>Request deletion of your data (subject to legal obligations).</li>
                        <li>Opt out of marketing communications.</li>
                      </ul>

                      <h3 className="text-base font-semibold text-gold">7. Third-Party Links</h3>
                      <p>
                        Our website may contain links to external sites. We are not responsible for
                        their privacy practices.
                      </p>

                      <h3 className="text-base font-semibold text-gold">8. Updates to This Policy</h3>
                      <p>
                        We may update this Privacy Policy periodically. Changes will be posted on this
                        page with an updated effective date.
                      </p>

                      <p className="mt-5">
                        For questions, contact us at{' '}
                        <a
                          href="mailto:official.laynered@gmail.com"
                          className="text-gold hover:underline"
                          style={{ color: gold }}
                        >
                          official.laynered@gmail.com
                        </a>
                        .
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        At Layne Red, we strive to ensure your satisfaction with our products.
                        This Exchange Policy outlines the conditions under which exchanges are
                        permitted.
                      </p>

                      <h3 className="text-base font-semibold text-gold">1. Eligibility for Exchange</h3>
                      <p>
                        Exchanges are only permitted for the same product in a different size. We do
                        not offer exchanges for different products or different colors of the same
                        product. The item must be:
                      </p>
                      <ul className="list-disc pl-5">
                        <li>Unused and in its original condition.</li>
                        <li>In its original packaging with all tags attached.</li>
                        <li>Requested within three (3) days of receiving the product.</li>
                      </ul>

                      <h3 className="text-base font-semibold text-gold">2. Exchange Process</h3>
                      <p>
                        To initiate an exchange:
                      </p>
                      <ul className="list-disc pl-5">
                        <li>
                          Contact us at{' '}
                          <a
                            href="mailto:official.laynered@gmail.com"
                            className="text-gold hover:underline"
                            style={{ color: gold }}
                          >
                            official.laynered@gmail.com
                          </a>{' '}
                          within three (3) days of delivery to request an exchange.
                        </li>
                        <li>
                          Return the original item to the seller. You are responsible for return
                          shipping costs unless the item is defective.
                        </li>
                        <li>
                          Once we receive and verify the returned item, we will place a new order for
                          the same product in the requested size, subject to availability.
                        </li>
                      </ul>

                      <h3 className="text-base font-semibold text-gold">3. Conditions</h3>
                      <p>
                        The exchanged order must be returned to the seller in its entirety. Partial
                        returns (e.g., only part of a bundled product) are not accepted. If the
                        requested size is unavailable, we will notify you and offer a refund or
                        alternative options.
                      </p>

                      <h3 className="text-base font-semibold text-gold">4. Non-Exchangeable Items</h3>
                      <p>
                        Items cannot be exchanged for a different color or a different product.
                        Exchanges are strictly limited to size changes for the same product SKU.
                      </p>

                      <h3 className="text-base font-semibold text-gold">5. Refunds for Exchanges</h3>
                      <p>
                        If an exchange cannot be fulfilled (e.g., due to stock unavailability), a
                        refund may be issued instead, processed to your original payment method within
                        7 business days of receiving the returned item.
                      </p>

                      <p className="mt-5">
                        For questions about exchanges, contact us at{' '}
                        <a
                          href="mailto:official.laynered@gmail.com"
                          className="text-gold hover:underline"
                          style={{ color: gold }}
                        >
                          official.laynered@gmail.com
                        </a>
                        .
                      </p>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
}

export default Footer;